import { motion } from 'framer-motion';
import React, { useState } from 'react';
import OrderForm from '../../../forms/order/OrderForm';
import PriceList from '../../../components/global/order/PriceList';
import LocalShipping from '@mui/icons-material/LocalShipping';
import getIconForDeliveryMethod from '../../../helperFunctions/getIconForDeliveryMethod';
import translateDevlieryMethod from '../../../helperFunctions/translateDeliveryMethod';
import { useOrder } from '../../../context/OrderContext';
import { DeliveryMethod, OrderCreationAttributes } from '../../../models/Order';
import { Order as OrderModel } from '../../../models/Order';
import { CartProduct } from '../Cart';
import { Offer } from '../../../models/Offer';
import { useOffer } from '../../../context/OfferContext';

type Props = {
  itemVariants: any;
  shoppingList: CartProduct[];
  setActiveStep: (step: number) => void;
  deliveryCosts: { deliveryCost: number; deliveryMethod: DeliveryMethod }[];
  offer?: Offer;
  orderType?: 'standard' | 'offer';
};

function Order({
  itemVariants,
  shoppingList,
  setActiveStep,
  deliveryCosts,
  offer,
  orderType = 'standard',
}: Props) {
  const { createOrder } = useOrder();
  const { createOrderFromOffer } = useOffer();

  const [deliveryPrice, setDeliveryPrice] = useState<number>(0);

  const submitOrderForm = async (
    data: OrderCreationAttributes,
  ): Promise<OrderModel | null> => {
    if (orderType === 'standard') {
      const order = await createOrder(
        data,
        shoppingList.map((product) => ({
          id: product.id,
          quantity: product.quantity,
        })),
      );      
      setActiveStep(2); // Przejście do kroku płatności
      return order;
    } else if (orderType === 'offer' && offer) {
      const order = await createOrderFromOffer(offer.offerNumber!, data);
      setActiveStep(2); // Przejście do kroku płatności
      return order;
    }

    return null;
  };

  return (
    <motion.div
      className="grid grid-cols-1 lg:grid-cols-3 gap-8"
      variants={itemVariants}
    >
      {/* Formularz zamówienia + trasy */}
      <div className="col-span-1 lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <OrderForm
          onSubmit={submitOrderForm}
          deliveryCosts={deliveryCosts}
          setDeliveryPrice={setDeliveryPrice}
          orderType={orderType}
          offer={offer}
        />
      </div>

      {/* Panel boczny z podsumowaniem */}
      <motion.div className="col-span-1" variants={itemVariants}>
        <div className="sticky top-24 space-y-6">
          <PriceList products={shoppingList} deliveryPrice={deliveryPrice} />

          {/* Informacje o dostawie */}
          {deliveryCosts.length > 0 && (
            <motion.div
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              variants={itemVariants}
            >
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center">
                  <LocalShipping
                    className="text-gray-500 mr-2"
                    fontSize="small"
                  />
                  <h3 className="text-base font-medium text-gray-800">
                    Dostępne metody dostawy
                  </h3>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {deliveryCosts.map((option) => (
                  <div
                    key={option.deliveryMethod}
                    className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-b-0 last:pb-0"
                  >
                    <div className="flex items-center">
                      <img
                        src={getIconForDeliveryMethod(option.deliveryMethod)}
                        alt={option.deliveryMethod}
                        className="h-6 mr-2"
                      />
                      <span className="text-sm">
                        {translateDevlieryMethod(option.deliveryMethod)}
                      </span>
                    </div>
                    <span className="font-medium text-sm">
                      {option.deliveryCost.toFixed(2)} zł
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Dodatkowe informacje */}
          <motion.div
            className="text-xs text-gray-500 p-4 bg-white rounded-lg shadow-sm border border-gray-200"
            variants={itemVariants}
          >
            <p className="mb-2">
              <strong>Bezpieczne zakupy:</strong> Wszystkie transakcje są
              chronione szyfrowaniem SSL.
            </p>
            <p>
              <strong>Potrzebujesz pomocy?</strong> Skontaktuj się z naszym
              zespołem obsługi klienta pod numerem telefonu
              <a
                href="tel:+48123456789"
                className="text-primary hover:underline ml-1"
              >
                +48 604 856 094
              </a>
            </p>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default Order;
